import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="site-container">
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 font-heading mb-2">文章未找到</h1>
              <p className="text-foreground-500 text-sm mb-4">该文章不存在或已被删除</p>
              <Link
                to="/articles"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="site-container">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '影视资讯', path: '/articles' },
              { label: article.title },
            ]}
          />

          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-600 text-xs">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight font-heading">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 mt-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.cover}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:font-heading
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200 flex items-center justify-between">
              <Link
                to="/articles"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
              <div className="flex items-center gap-3">
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        <section className="py-10 md:py-14 mt-10 bg-background-100">
          <div className="site-container">
            <h2 className="text-xl font-bold text-foreground-900 mb-6 font-heading">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles
                .filter((a) => a.id !== article.id)
                .slice(0, 3)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.cover}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs mb-2 inline-block">
                        {related.category}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900 leading-snug line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.date,
            author: {
              "@type": "Organization",
              name: article.author
            },
            publisher: {
              "@type": "Organization",
              name: "蚂蚁影视",
              url: "https://www.mantfilm.com"
            },
            isPartOf: {
              "@type": "WebSite",
              name: "蚂蚁影视",
              url: "https://www.mantfilm.com"
            }
          }, null, 2)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.mantfilm.com" },
              { "@type": "ListItem", position: 2, name: "影视资讯", item: "https://www.mantfilm.com/articles" },
              { "@type": "ListItem", position: 3, name: article.title }
            ]
          }, null, 2)
        }}
      />
    </>
  );
}